import logging from '~/stores/providers/logging';
import Future, { FutureInstance, isFuture, parallel, RejectFunction, ResolveFunction } from 'fluture';
import { curry, isNil } from 'ramda';
import { Either, Maybe } from 'ramda-fantasy';

export const lift = curry(<T, U>(f: (value: T) => U, value: T): Maybe<U> => {
  return isNil(value) ? Maybe.Nothing() : Maybe.Just(value).map(f);
});

export const liftPredicate = curry(<T>(predicate: (v: T) => boolean, v: T): Maybe<T> => {
  return predicate(v) ? Maybe.Just(v) : Maybe.Nothing();
});

export const liftEither = curry((f, value) => {
  return isNil(value) ? Either.Left(value) : Either.Right(value).map(f);
});

export const liftFuture = curry((f, value) => {
  return Future.of(value).map(f);
});

export const chain = curry((fn, moand: Record<'chain', any>) => moand.chain(fn));

export const map = curry((f, container) => container.map(f));

export const getOrElse = curry((message, container) => container.getOrElse(message));

export const getOrElseThrow = curry((a, container) => container.getOrElseThrow(a));

export const orElse = curry((f, container) => container.orElse(f));

export const then = curry((fn, thenable) => thenable.then(fn));

export const catchP = curry((fn, promise) => promise.catch(fn));

export const run = curry((container) => container.run());

export const liftPromise = (value) => Promise.resolve(value);

export const ioToMaybe = (io) => {
  const result = io.runIO();
  return result ? Maybe.of(result) : Maybe.Nothing();
};

export const ioToEither = curry((leftValue, io) => {
  const result = io.runIO();
  return isNil(result) ? Either.Left(leftValue) : Either.Right(result);
});

// liftA2 :: (Applicative f) => (a1 -> b) -> f a1 -> f b;
export const liftA = (g, f1) => f1.map(g);

// liftA2 :: Monad M => (a -> M b) -> M a -> M b -> M b;
export const liftA2 = (g, f1, f2) => f1.map(g).ap(f2);

export const mEither = curry(<L, R, T>(left: (v: L) => T, right: (v: R) => T, either: Either<L, R>): T => {
  return Either.isLeft(either) ? left(either.value) : right(either.value as R);
});

// mMaybe :: b -> (a -> b) -> Maybe a -> b
export const mMaybe = curry(<Default, V, R>(v: Default, f: (value: V) => R, m: Maybe<V>): Default | R => {
  if (m.isNothing) {
    return v;
  }

  return f(m.value);
});

export const maybeOr = curry(<Default, R, V>(v: () => Default, f: (value: V) => R, m: Maybe<V>): Default | R => {
  if (m.isNothing) {
    return v();
  }
  return f(m.value);
});

export const runFutureFork = curry(
  <R>(onFail: RejectFunction<any>, onSuccess: ResolveFunction<R>, future: FutureInstance<any, R>) => {
    return isFuture(future) ? future.fork(onFail, onSuccess) : future;
  }
);

export const runFutureValue = curry(<R>(onSuccess: ResolveFunction<R>, future: FutureInstance<any, R>) => {
  return runFutureFork(logging.error, onSuccess, future);
});

export const parallelFutures = curry(<L, R>(length: number, futures: FutureInstance<L, R>[]) => {
  return parallel(length, futures);
});

export const runIO = curry((fn, io) => {
  return io.map(fn).runIO();
});
